import { useState } from 'react'
import { displayAction } from '../../../lib/displayApi'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

const TABS = ['Schedule', 'Tasks', 'Chores', 'More']

export function TeenPersonalView({ member, data, deviceToken, onBack }) {
  const [tab, setTab]   = useState(0)
  const [tasks, setTasks] = useState(data.tasks ?? [])

  const memberColor = MEMBER_COLORS[member.memberColor] ?? '#8b5cf6'
  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]

  const myEvents = (data.events ?? [])
    .filter((e) => (e.event_members ?? []).some((em) => em.user_id === member.id))
    .filter((e) => new Date(e.start_time) >= today)
    .sort((a, b) => a.start_time.localeCompare(b.start_time))

  const myTasks  = tasks.filter((t) => t.assigned_to === member.id && !t.completed_at)
  const doneTasks = tasks.filter((t) => t.assigned_to === member.id && t.completed_at)

  async function completeTask(taskId) {
    try {
      await displayAction(deviceToken, member.id, 'complete_task', taskId)
      setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, completed_at: new Date().toISOString() } : t))
    } catch (_) {}
  }

  const Schedule = () => {
    let lastDate = ''
    return (
      <div>
        {myEvents.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-400 text-base">No upcoming events</p>
          </div>
        ) : myEvents.map((e) => {
          const d = e.start_time.split('T')[0]
          const showDate = d !== lastDate
          lastDate = d
          return (
            <div key={e.id}>
              {showDate && (
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mt-4 mb-1.5">
                  {new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              )}
              <div className="flex items-center gap-3 bg-white/8 rounded-xl px-3 py-2.5 mb-1.5">
                <span className="text-gray-500 text-xs w-16 flex-shrink-0">
                  {e.is_all_day ? 'All day' : new Date(e.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{e.title}</p>
                  {e.location && <p className="text-gray-500 text-xs truncate">{e.location}</p>}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const TaskList = ({ items, showComplete }) => (
    <div className="space-y-1.5">
      {items.map((task) => (
        <div key={task.id} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${showComplete ? 'bg-white/8' : 'opacity-50'}`}>
          {showComplete ? (
            <button
              onClick={() => completeTask(task.id)}
              className="w-5 h-5 rounded border-2 border-gray-500 hover:border-emerald-400 flex items-center justify-center flex-shrink-0 transition-colors"
            />
          ) : (
            <div className="w-5 h-5 rounded bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs">✓</span>
            </div>
          )}
          <p className={`text-sm flex-1 min-w-0 truncate ${showComplete ? 'text-white' : 'text-gray-500 line-through'}`}>{task.title}</p>
          {task.due_date && showComplete && (
            <span className="text-gray-500 text-xs flex-shrink-0">
              {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
      ))}
    </div>
  )

  const More = () => (
    <div className="space-y-2.5">
      <div className="bg-white/8 rounded-xl p-3">
        <p className="text-gray-400 text-xs font-semibold uppercase mb-1.5">Tonight's Dinner</p>
        {(() => {
          const slot = (data.mealSlots ?? []).find((s) => s.date === todayStr && s.slot_name === 'dinner')
          return <p className="text-white text-base">{slot?.recipe?.title ?? slot?.custom_name ?? 'Not planned'}</p>
        })()}
      </div>
      {(data.announcements ?? []).length > 0 && (
        <div className="bg-white/8 rounded-xl p-3">
          <p className="text-gray-400 text-xs font-semibold uppercase mb-1.5">Announcements</p>
          {(data.announcements ?? []).slice(0, 2).map((a) => (
            <div key={a.id} className="mb-2 last:mb-0">
              <p className="text-white font-medium text-sm">{a.title}</p>
              <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{a.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col bg-gray-950">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: memberColor }}
          >
            {member.displaySettings?.avatar_emoji ?? member.displayName?.[0]}
          </div>
          <span className="text-white font-semibold text-sm sm:text-base">{member.displayName}</span>
        </div>
        <button onClick={onBack} className="text-gray-400 hover:text-white text-xs sm:text-sm px-3 py-1.5 rounded-lg bg-white/10 transition-colors">
          ← Back
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 flex-shrink-0">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`flex-1 py-2.5 text-xs sm:text-sm font-semibold transition-colors ${tab === i ? 'text-white border-b-2' : 'text-gray-500'}`}
            style={tab === i ? { borderColor: memberColor } : {}}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-5 min-h-0">
        {tab === 0 && <Schedule />}
        {tab === 1 && (
          <div>
            {myTasks.length === 0 && doneTasks.length === 0 ? (
              <p className="text-gray-400 text-center py-8 text-base">No tasks assigned</p>
            ) : (
              <>
                <TaskList items={myTasks} showComplete={true} />
                {doneTasks.length > 0 && (
                  <div className="mt-4">
                    <p className="text-gray-600 text-xs uppercase tracking-wider mb-1.5">Completed</p>
                    <TaskList items={doneTasks} showComplete={false} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {tab === 2 && <TaskList items={myTasks} showComplete={true} />}
        {tab === 3 && <More />}
      </div>
    </div>
  )
}
